import {
	debounce,
	getAttributeOrThrow,
	getAttributeOrUndefined,
	qsaOptional,
	qsOptional,
	qsRequired,
	targetRequired,
} from '../core/global'
import {
	getPayloadForLineUpdate,
	cartSectionsToRender,
	applyUpdatesFromSectionApi,
} from '@/scripts/core/features'
import { fetchConfig } from '../core/global'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { CartShippingBar } from '@/scripts/cart/CartShippingBar'

type OnChangeEvent = {
	index: number
	value: number
	name?: string
	key: string | boolean
}

export type LineItemCustom = {
	variantId: string
	isSubscription: boolean
	currentQuantity: number
	index: number
	key: string
	updateQuantity: number
	price: number
	originalPrice: number
	optionsGwpKeyToRemove?: string
}

export class CartDrawerItems extends UcoastEl {
	static htmlSelector = 'cart-drawer-items'
	lineItemStatusElement: HTMLElement
	shippingBar?: CartShippingBar
	total: number
	beforeDebounce: (event: InputEvent) => void
	debouncedOnChange: (event: InputEvent) => void
	lineItems: LineItemCustom[]

	constructor() {
		super()
		this.total = this.getTotal()

		this.lineItemStatusElement = qsRequired('#CartDrawer-LineItemStatus')

		this.shippingBar = qsOptional('cart-shipping-bar')

		this.lineItems = this.getLineItems()

		this.debouncedOnChange = debounce((event: InputEvent) => {
			const update = this.getEventUpdate(event, false)
			this.updateTotal()
			this.onChange(update)
		}, 100)

		this.beforeDebounce = (event: InputEvent) => {
			const update = this.getEventUpdate(event, false)
			this.enableLoading(update.index)
			this.immediateUpdate(update)
			this.updateTotal()
			this.debouncedOnChange(event)
		}

		this.updateTotal.bind(this)

		this.addEventListener('change', this.beforeDebounce.bind(this))
	}

	onChange({ index }: OnChangeEvent) {
		this.updateQuantity(index)
	}

	immediateUpdate({ index, value, key }: OnChangeEvent) {
		const i = index - 1
		const qty = value
		if (this.lineItems.length <= i) return // make sure index exists in array
		const item = this.lineItems[i]
		if (key !== item.key) return // make sure selected index applies to the same key as the original event
		item.updateQuantity = qty
		if (item.updateQuantity < 1) {
			const items = this.querySelectorAll('.cart-item')
			items[i].classList.add('hidden')
		}
	}

	getTotal(): number {
		const currentTotalEl = qsOptional('[data-uc-cart-total-read-only]')
		if (!currentTotalEl) {
			return this.lineItems.reduce((accumulator, object) => {
				return accumulator + object.price * object.updateQuantity
			}, 0)
		} else {
			return parseInt(
				getAttributeOrThrow(
					'data-uc-cart-total-read-only',
					currentTotalEl
				)
			)
		}
	}

	updateShippingBar() {
		this.shippingBar?.liveUpdateMeter(this.total)
	}

	updateTotal() {
		this.total = this.getTotal()
		this.updateShippingBar()
	}

	getEventUpdate(event: InputEvent, debounced: boolean): OnChangeEvent {
		const activeElement = document.activeElement

		const target: HTMLInputElement = targetRequired(event)
		if (!(activeElement instanceof HTMLElement)) {
			throw new Error('No active element')
		}

		const update: OnChangeEvent = {
			index: parseInt(getAttributeOrThrow('data-index', target)),
			value: parseInt(target.value),
			name: getAttributeOrUndefined('name', activeElement),
			key: getAttributeOrUndefined('data-key', activeElement) ?? false,
		}

		if (debounced) return update
		const i = update.index - 1
		if (this.lineItems.length <= i) return { ...update, key: false }
		const item = this.lineItems[i]
		if (item.key !== update.key) return { ...update, key: false }
		return update
	}

	getLineItems() {
		const items = qsaOptional('.cart-item', this)
		if (!items) return []
		const mappedItems = Array.from(items).map((item, index) => {
			return this.getLineItem(item, index)
		})
		return mappedItems
	}

	getLineItem(item: HTMLElement, index: number): LineItemCustom {
		const key = getAttributeOrThrow('data-key', item)
		const variantId = getAttributeOrThrow('data-variant', item)

		return {
			key,
			index,
			currentQuantity: parseInt(
				getAttributeOrThrow('data-current-quantity', item)
			),
			updateQuantity: parseInt(
				qsRequired<HTMLInputElement>('[type="number"]', item).value
			),
			variantId,
			isSubscription: item.getAttribute('data-subscription') !== null,
			price: parseInt(getAttributeOrThrow('data-price', item)),
			originalPrice: parseInt(
				item.getAttribute('data-original-price') ?? '0'
			),
			optionsGwpKeyToRemove: getAttributeOrUndefined(
				'data-options-gwp-key-to-remove',
				item
			),
		}
	}

	updateQuantity(line: number) {
		const lineUpdateEl = qsRequired(
			`[data-line-item][data-line="${line}"]`,
			this
		)
		const lineItemToUpdate = this.getLineItem(lineUpdateEl, line)
		const updates = getPayloadForLineUpdate(lineItemToUpdate)

		const updatesBody = {
			updates,
			sections: cartSectionsToRender()
				.map((section) => section.section)
				.join(','),
			sections_url: window.location.pathname,
		}

		const updateUrl = `${window.routes.cart_update_url}.js`

		fetch(updateUrl, {
			...fetchConfig(),
			body: JSON.stringify(updatesBody),
		})
			.then((response) => {
				return response.json()
			})
			.then((parsedState) => {
				applyUpdatesFromSectionApi({
					sections: parsedState.sections,
					payload: {
						type: 'line_update',
						lineItem: lineItemToUpdate,
					},
				})
				//this.updateLiveRegions(line, parsedState.item_count)
				this.disableLoading()
			})
			.catch((e) => {
				console.log('caught error on update', e)
				this.querySelectorAll('.loading-overlay').forEach((overlay) =>
					overlay.classList.add('hidden')
				)
				this.disableLoading()
			})
			.finally(() => {
				this.lineItems = this.getLineItems()
				this.updateTotal()
			})
	}

	enableLoading(line: number) {
		const mainCartItems = qsOptional('#CartDrawer-CartItems')
		mainCartItems?.classList.add('cart__items--disabled')

		this.querySelectorAll(
			`#CartDrawer-Item-${line} .loading-overlay`
		).forEach((overlay) => overlay.classList.remove('hidden'))

		const cartDrawerItems = this.querySelectorAll(
			`#CartDrawer-Item-${line}`
		)
		cartDrawerItems.forEach((item) => item.classList.add('loading'))

		const activeElement = document.activeElement
		if (activeElement instanceof HTMLElement) {
			activeElement.blur()
		}
		this.lineItemStatusElement.setAttribute('aria-hidden', 'false')
	}

	disableLoading() {
		const mainCartItems = document.getElementById('CartDrawer-CartItems')
		mainCartItems?.classList.remove('cart__items--disabled')

		const cartDrawerLoadingItems = qsaOptional('.cart-item.loading', this)
		cartDrawerLoadingItems?.forEach((item) =>
			item.classList.remove('loading')
		)
	}
}
